/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { UncontrolledTooltip, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { observable, reaction } from 'mobx';
import classnames from 'classnames';

import { INosSubscriptionListData } from './nos-subscription-list.data';
import { INosSubscriptionListProps } from './nos-subscription-list.props.autogenerated';
import { IGetSubscriptionListData } from '../../actions/get-subscription-list.action';
import { formatDate, getSubscriptionStatus } from './utils/nos-subscription-list.utils';
import NosSubscriptionDetailButton from './components/nos-subscription-detail-button';

export interface INosSubscriptionListViewProps extends INosSubscriptionListProps<INosSubscriptionListData> {
    table: INodeProps;
    heading: React.ReactNode;
    className?: string;
}

export interface INosSubscriptionListExtendedProps extends INosSubscriptionListProps<INosSubscriptionListData> {}

/**
 * Interface for INosSubscriptionListState.
 */
export interface INosSubscriptionListState {}

/**
 *
 * NosSubscriptionList component
 * @extends {React.Component<INosSubscriptionListProps<INosSubscriptionListData>, INosSubscriptionListState>}
 */
class NosSubscriptionList extends React.Component<INosSubscriptionListProps<INosSubscriptionListData>, INosSubscriptionListState> {
    @observable private subscriptionListData?: IGetSubscriptionListData[];

    public constructor(props: INosSubscriptionListExtendedProps) {
        super(props);

        const { data } = this.props;

        this.subscriptionListData = data.subscriptionList?.result || [];
    }

    public componentDidMount(): void {
        if (this.props.context.actionContext.requestContext.user.isAuthenticated) {
            // Initializing data props
            this._dataInitialize(this.props);
        }
    }

    public shouldComponentUpdate(nextProps: INosSubscriptionListExtendedProps, nextState: INosSubscriptionListState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            className: classnames('nos-subscription-list', this.props.config.className),
            heading: this.props.config.heading && (
                <Msdyn365.Text
                    className=''
                    tag={this.props.config.heading.tag || 'h1'}
                    text={this.props.config.heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            table: this._renderTable(this.subscriptionListData ?? [])
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderTooltip = () => {
        const { resources } = this.props;

        return (
            <>
                <i
                    id='subscriptionStatusTooltip'
                    className='nos-subscription-list__tooltip-icon ms-icon-custom ms-icon-custom--circleInfo'
                    aria-describedby='subscriptionStatusTooltipContent'
                />
                {/* TODO: possible bug: individual props like placement are not working */}
                <UncontrolledTooltip target='subscriptionStatusTooltip' id='subscriptionStatusTooltipContent'>
                    {<div dangerouslySetInnerHTML={{ __html: resources.subscriptionStatusTooltip }} />}
                </UncontrolledTooltip>
            </>
        );
    };

    private _renderTable = (subscriptionList: IGetSubscriptionListData[]) => {
        const { resources } = this.props;
        return (
            <React.Fragment>
                {subscriptionList?.length > 0 ? (
                    <div className='table-responsive'>
                        <table className='table table-striped'>
                            <thead>
                                <tr>
                                    <th scope='col'>{resources.subscriptionIdLabel}</th>
                                    <th scope='col'>{resources.subscriptionProductLabel}</th>
                                    <th scope='col'>
                                        {resources.subscriptionStatusLabel}
                                        {this._renderTooltip()}
                                    </th>
                                    <th scope='col'>{resources.nextPaymentLabel}</th>
                                    <th scope='col'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptionList?.map((el, index) => {
                                    return (
                                        <tr key={el.SubBillBillingScheduleNumber + el.SubBillLineNum.toString() + String(index)}>
                                            <th scope='row'>{el.SubBillBillingScheduleNumber}</th>
                                            <td>{el.SubBillItemName}</td>
                                            <td>{getSubscriptionStatus(resources, el.SubBillScheduleStatus)}</td>
                                            <td>
                                                {el.SubBillStartDate ? formatDate(this.props.context, new Date(el.SubBillStartDate)) : '-'}
                                            </td>
                                            <td>
                                                <NosSubscriptionDetailButton
                                                    actionContext={this.props.context.actionContext}
                                                    buttonName={resources.viewEditLabel}
                                                    LineId={el.SubBillLineNum}
                                                    SubscriptionId={el.SubBillBillingScheduleNumber}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div>{resources.noSubscriptions}</div>
                )}
            </React.Fragment>
        );
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: INosSubscriptionListExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.subscriptionList?.result,
            () => {
                this.subscriptionListData = data.subscriptionList?.result ?? [];
            }
        );
    };
}

export default NosSubscriptionList;
